import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const HEADER_OFFSET = 104; // sticky header + safe buffer (matches scroll-mt-24 ~96px)
const MAX_HASH_RETRY_MS = 2500;
const HASH_RETRY_INTERVAL_MS = 120;

function scrollToAnchor(hash: string, behavior: ScrollBehavior = "smooth") {
  const id = hash.replace("#", "");
  if (!id) return false;
  const element = document.getElementById(id);
  if (!element) return false;

  const top = element.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top, behavior });
  return true;
}

export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const previousPathname = useRef(pathname);

  // On pathname change: if there's a hash, scroll to that anchor after the
  // DOM has settled; otherwise scroll to top. This prevents lazy-loaded
  // routes/images/motion layouts from pushing the target out of position.
  useEffect(() => {
    const pathChanged = previousPathname.current !== pathname;
    previousPathname.current = pathname;

    if (hash) {
      // Retry until the target element exists, so lazy-loaded routes and
      // deferred renders don't beat us to the scroll.
      const start = Date.now();
      let intervalId: number | undefined;

      const tryScroll = () => {
        const found = scrollToAnchor(hash, "auto");
        if (found || Date.now() - start > MAX_HASH_RETRY_MS) {
          window.clearInterval(intervalId);
        }
      };

      // Initial attempt after a short paint, then poll until found.
      window.setTimeout(() => {
        tryScroll();
        if (!document.getElementById(hash.replace("#", ""))) {
          intervalId = window.setInterval(tryScroll, HASH_RETRY_INTERVAL_MS);
        }
      }, 50);

      return () => {
        if (intervalId) window.clearInterval(intervalId);
      };
    }

    if (pathChanged) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

export { scrollToAnchor };
